/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { LucideIcon, UsersIcon } from 'lucide-react'

type FilterableKit = 'mta-insights' | 'mta-mini' | 'mta-team-kit' | 'mta-select'

type KitFilter = {
  [key in FilterableKit]: Array<KitFilterDetails>
}

type KitFilterDetails = {
  title: string
  description: string
  icon: LucideIcon
  activities: Array<string>
}

const kitFilters: KitFilter = {
  'mta-insights': [
    {
      title: 'Tenacious Teamworkers',
      description:
        'With 18 dynamic activities, you’ll find options to energize teams, foster teamwork, and challenge beliefs, even at senior levels.',
      icon: UsersIcon,
      activities: [
        'group-necklace',
        'short-thread',
        'wheelbarrow',
        'boxed-in',
        'enclosed',
        'tall-stack',
        'tightly-packed',
        'odd-type-of-stack',
        'balancing-act',
        'shapes',
        'minefield',
        'diy',
        'feet-of-clay',
        'my-success',
        'spinning-your-web',
        'caught-in-a-web',
        'successful-pairs',
        'speedy-stack',
      ],
    },
    {
      title: 'Competent Communicators',
      description:
        'Use these 7 activities to enhance communication by addressing style, content, attitudes, and its two-way nature, improving clarity and teamwork.',
      icon: UsersIcon,
      activities: [
        'getting-it-right',
        'sorting-shapes',
        'blind-statues',
        'my-views',
        'swinging',
        'seeing-red',
        'back-to-back',
      ],
    },
    {
      title: 'Authentic Leaders',
      description:
        'These 9 activities help participants grow as leaders, from new supervisors to regional team managers. They explore leadership style impacts and ways to inspire their teams effectively.',
      icon: UsersIcon,
      activities: [
        'taller-stack',
        'led-statues',
        'led-jigsaw',
        'leading-from-afar',
        'leading-the-team',
        'cable-car',
        'the-hoist',
        'waste-away',
        'maxi-market',
      ],
    },
    {
      title: 'Creative Problem Solvers',
      description:
        'These 6 activities help participants challenge assumptions, define problems effectively, and overcome emotional, cultural, or intellectual blocks. They’ll refine problem-solving skills and apply them to real challenges.',
      icon: UsersIcon,
      activities: ['colourful-necklace', 'girders', 'jigsaw', 'odd-colour-of-stack', 'water-tower', 'through-the-slot'],
    },
    {
      title: 'Stakeholder Champions',
      description:
        'These 6 activities help teams align with customers and stakeholders, focusing on strategic planning, customer orientation, and value-driven operations for organizational success.',
      icon: UsersIcon,
      activities: [
        'disc-roll',
        'our-success',
        'meeting-expectations',
        'leading-pairs',
        'meeting-the-challenge',
        'everyone-is-a-winner',
      ],
    },
    {
      title: 'Decisive Decision Makers',
      description:
        'These 6 activities challenge and develop the skills and attitudes needed by top performers, tackling complex leadership tasks to improve decision-making and effectiveness.',
      icon: UsersIcon,
      activities: [
        'delivered-on-time',
        'build-to-design',
        'money-matters',
        'artifacts',
        'container-freight',
        'getting-ahead',
        'disc-transporter',
      ],
    },
  ],
  'mta-mini': [
    {
      title: 'Effective Interactions',
      description:
        'Four energizing activities introduce teamwork essentials. From "Handcuffed," an ice-breaker exploring planning and collaboration, to "Helipads," which challenges teams to achieve success through inter-team cooperation and adaptability.',
      icon: UsersIcon,
      activities: ['handcuffed', 'gantry', 'within-four-walls', 'helipads'],
    },
    {
      title: 'Remote Workers That Get Results',
      description:
        'In "Transport," teams in separate locations collaborate to build a truck and skips. Success hinges on understanding the immediate impact of their actions, fostering strong communication and teamwork.',
      icon: UsersIcon,
      activities: ['transport'],
    },
    {
      title: 'Teams That Talk',
      description:
        'In "Carts and Horses," teams design and replicate models through verbal communication only—no writing or drawing. The task tests clarity, collaboration, and communication under constraints.',
      icon: UsersIcon,
      activities: ['carts-and-horses'],
    },
    {
      title: 'Project Teams That Perform',
      description:
        '"The Rig" challenges teams to build a profitable oil rig by managing costs, working across locations, and maintaining focus on the end goal, despite complex logistics.',
      icon: UsersIcon,
      activities: ['the-rig'],
    },
    {
      title: 'Change Champions',
      description:
        '"Words, Names and Numbers" begins with independent teams and later merges them into one. The activity highlights challenges in adapting to change and capitalizing on new opportunities.',
      icon: UsersIcon,
      activities: ['words-names-numbers'],
    },
    {
      title: 'Savvy Situational Leaders',
      description:
        'In "Promises, Promises," leaders balance customer demands and team constraints under pressure. The task underscores the importance of prioritization, resource management, and leadership adaptability.',
      icon: UsersIcon,
      activities: ['promises-promises'],
    },
  ],
  'mta-select': [
    {
      title: 'Adding Value',
      description:
        'Participants explore customer needs, think critically, and adapt decisions. Negotiation and competition can be incorporated.',
      icon: UsersIcon,
      activities: ['adding-value'],
    },
    {
      title: 'Name Your Price',
      description: 'Generate profits by gathering information, making decisions, and managing resources effectively.',
      icon: UsersIcon,
      activities: ['name-your-price'],
    },
    {
      title: 'Back to Basics',
      description:
        'A fast-paced activity requiring planning, quick action, and handling unexpected challenges. Simple, yet highly revealing.',
      icon: UsersIcon,
      activities: ['back-to-basics'],
    },
    {
      title: 'Tunnels',
      description:
        'Interpret complex briefs while showcasing teamwork, problem-solving, and communication skills to achieve goals.',
      icon: UsersIcon,
      activities: ['tunnels'],
    },
    {
      title: 'Improve Performance',
      description:
        'A challenging task that builds on previous activities, focusing on self-awareness and emotional intelligence.',
      icon: UsersIcon,
      activities: ['improving-performance'],
    },
    {
      title: 'Making a Stand',
      description:
        'Participants link their strengths, presenting business solutions that showcase strategic and personal skills.',
      icon: UsersIcon,
      activities: ['making-a-stand'],
    },
    {
      title: 'Personal Pizzas',
      description:
        'Career development tool that combines individual and group work for self-reflection and future planning.',
      icon: UsersIcon,
      activities: ['personal-pizzas'],
    },
    {
      title: 'Just 35 Minutes',
      description:
        'An in-tray exercise challenging participants to prioritize critical issues and make effective decisions.',
      icon: UsersIcon,
      activities: ['just-35-minutes'],
    },
  ],
  'mta-team-kit': [
    {
      title: 'High-Performing Teams',
      description:
        'Over the Bridge develops team skills through learning and application. Teams clarify objectives, question stakeholders, and plan effectively to build a bridge and car from ambiguous instructions.',
      icon: UsersIcon,
      activities: ['over-the-bridge'],
    },
    {
      title: 'Capitalising on Diversity',
      description:
        'Rectangle explores team diversity through a blindfolded task requiring alignment on shared goals. It highlights communication styles and supports models like Belbin, DISC, or Tuckman.',
      icon: UsersIcon,
      activities: ['rectangle'],
    },
    {
      title: 'Cooperative Collaborators',
      description:
        'Digital Display is a silent activity exploring how personal actions impact team success. It highlights the balance between individual goals and supporting others to ensure overall team achievement.',
      icon: UsersIcon,
      activities: ['digital-display'],
    },
    {
      title: 'Big Picture Thinkers',
      description:
        'The Frame fosters strategic focus and teamwork. Two separated sub-teams must overcome frustration and poor communication to align on solving the bigger problem together.',
      icon: UsersIcon,
      activities: ['the-frame'],
    },
    {
      title: 'Empowering Leaders',
      description:
        'The Trailer explores leadership and delegation. Leaders guide from an overview but often struggle to empower teams, highlighting roles and responsibilities in effective teamwork.',
      icon: UsersIcon,
      activities: ['the-trailer'],
    },
    {
      title: 'Win-Win Negotiators',
      description:
        'The Tower enhances negotiation, conflict management, and trust-building. Teams must plan, communicate, and collaborate effectively to achieve win-win outcomes and long-term success.',
      icon: UsersIcon,
      activities: ['tower'],
    },
    {
      title: 'Trusting Team Members',
      description:
        '10 short activities introduce team concepts, energize groups, and spark competition. Quick yet impactful, they highlight group dynamics. Includes facilitator notes, reusable briefs, and learning review tools.',
      icon: UsersIcon,
      activities: [
        'where-is-the-chair',
        'the-stack',
        'getting-to-know-you',
        'through-the-bolt-hole',
        'domino-theory',
        'hold-everything',
        'rectangles',
        'roulette-wheel',
        'playhouse',
        'creating-obstacles',
        'pack-the-bags',
      ],
    },
  ],
}

export { kitFilters }
export type { FilterableKit, KitFilter, KitFilterDetails }
